'use client'

import React, { FC, ReactElement } from 'react'
import { CommandDialog, CommandGroup, CommandInput, CommandList } from '@/components/ui/command'
import Event from '@/utils/event'
import { useRouter } from 'next/navigation'

import Suggestions, { COMMANDS } from './pages/suggestions'

const EVENT = 'command'

const KBD =
	'inline-flex h-4 min-w-4 select-none items-center justify-center rounded border bg-muted px-1 font-mono text-[10px] text-muted-foreground'

export interface Command {
	featured: boolean
	icon?: ReactElement
	component?: FC<{ onNavigate: (v: string) => void; onClose: () => void; args?: any }>
	onClick?: ({ router, onClose, logout }: any) => void
	args?: any
	name: string
	heading?: string
}

export interface CommandPaletteHandle {
	open: () => void
}

interface CommandPaletteProps {
	ref?: React.Ref<CommandPaletteHandle>
	/// When set, the bus/keyboard listeners register only if this element is
	/// visible at mount — the header mounts one SearchInput per breakpoint and
	/// only the visible one may answer a `command` event. A headless host
	/// omits it and always answers.
	triggerRef?: { current: HTMLElement | null }
	/// The `/` shortcut. A host on a surface with its own `/` binding (the
	/// /alkanes token search) turns it off; the bus listener stays.
	hotkey?: boolean
	/// Extra classes on the dialog surface. The dialog portals to <body>, so a
	/// scoped-token host (the /alkanes app) passes its portal escape hatch
	/// (`alkanes-ink`) to keep its own register.
	contentClassName?: string
}

/// The site's command palette — the dialog half of SearchInput, extracted so
/// a page without the site header (the /alkanes app) can host the SAME
/// palette headlessly. Two hosts, one palette: SearchInput renders the search
/// button and hands it over as `triggerRef`; a headless host renders nothing
/// and the palette appears when a `command` event arrives on the bus.
const CommandPalette = (props: CommandPaletteProps): ReactElement => {
		const { ref, triggerRef, hotkey = true, contentClassName } = props
		const [open, setOpen] = React.useState(false)
		const walletConnectionRef = React.useRef(false)
		const [value, setValueState] = React.useState('')
		const [prevValue, setPrevValue] = React.useState('')
		const [pages, setPages] = React.useState<Command[]>([])
		// Whatever held focus when the palette opened gets it back on close.
		const openerRef = React.useRef<HTMLElement | null>(null)
		// Latest open/pages/remove handler for the document-level key listener,
		// which is registered once.
		const stateRef = React.useRef<{ open: boolean; pages: Command[] }>({ open: false, pages: [] })
		const removePageRef = React.useRef<() => void>(() => undefined)
		const page = pages[pages.length - 1]
		const router = useRouter()

		React.useImperativeHandle(
			ref,
			() => ({
				open: () => {
					openerRef.current = triggerRef?.current ?? (document.activeElement as HTMLElement)
					setOpen(true)
				}
			}),
			[triggerRef]
		)

		const setValue = React.useCallback(
			(v: any) => {
				if (pages.length == 0) {
					return setValueState(v)
				}
			},
			[pages]
		)

		React.useEffect(() => {
			if (triggerRef) {
				const trigger = triggerRef.current
				const isVisible =
					!!trigger &&
					!!(trigger.offsetWidth || trigger.offsetHeight || trigger.getClientRects().length)
				if (!isVisible) return
			}

			const handleEvent = (payload: any) => {
				openerRef.current = document.activeElement as HTMLElement
				const command = payload.command || (COMMANDS as any)[payload.id]
				walletConnectionRef.current = payload.id === 'connect-wallet'
				setValueState(command.name)
				setPages([command])
				setOpen(true)
			}

			const down = (e: KeyboardEvent) => {
				const tag = (e.target as HTMLElement)?.tagName
				if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) {
					return
				}
				// Backspace anywhere in an open palette page (focus left the box after
				// a mouse click) goes back to the previous page, like the arrow.
				if (e.key === 'Backspace' && stateRef.current.open && stateRef.current.pages.length) {
					e.preventDefault()
					removePageRef.current()
					return
				}
				if (hotkey && e.key === '/') {
					e.preventDefault()
					if (!stateRef.current.open) openerRef.current = document.activeElement as HTMLElement
					setOpen((open) => !open)
				}
			}

			document.addEventListener('keydown', down)
			Event.listen(EVENT, handleEvent)

			return () => {
				document.removeEventListener('keydown', down)
				Event.removeListener(EVENT, handleEvent)
			}
		}, [triggerRef, hotkey])

		React.useEffect(() => {
			if (!open && walletConnectionRef.current) {
				walletConnectionRef.current = false
				Event.emit('connect-wallet-closed')
			}
		}, [open])

		//
		// Global Functions
		//

		const handleClose = React.useCallback(() => {
			setOpen(false)
		}, [])

		const handleNavigate = React.useCallback(
			(v: any) => {
				router.push(v)
				handleClose()
			},
			[handleClose, router]
		)

		const handleAddPage = React.useCallback(
			(p: Command) => {
				setPages((prev) => [...prev, p])
				setPrevValue(value)
				setValueState(p.name)
			},
			[value]
		)

		const handleRemovePage = React.useCallback(() => {
			setPages((pages) => pages.slice(0, -1))
			setValueState(prevValue)
			setPrevValue('')
		}, [prevValue])
		React.useEffect(() => {
			removePageRef.current = handleRemovePage
			stateRef.current = { open, pages }
		}, [handleRemovePage, open, pages])

		const handleOpenChange = React.useCallback(
			(newOpen: any) => {
				setOpen(newOpen)
				!newOpen && handleClose()
			},
			[handleClose]
		)

		return (
			<CommandDialog
				open={open}
				onOpenChange={handleOpenChange}
				className={contentClassName}
				onCloseAutoFocus={(e) => {
					// Keep the current page mounted until the exit animation finishes.
					setValueState('')
					setPrevValue('')
					setPages([])
					if (openerRef.current?.isConnected) {
						e.preventDefault()
						openerRef.current.focus()
					}
				}}
				onOpenAutoFocus={(e) => {
					if (pages.length) {
						e.preventDefault()
					}
				}}
			>
				<CommandInput
					onKeyDown={(e) => {
						// Escape goes to previous page. Backspace does too while the box is
						// empty or still shows the page's own name (nothing typed yet), so
						// a keyboard user is not forced to reach for the X.
						const untouched = !value || value === page?.name
						if (pages.length && (e.key === 'Escape' || (e.key === 'Backspace' && untouched))) {
							e.preventDefault()
							handleRemovePage()
						}
					}}
					placeholder="Search..."
					value={value}
					onValueChange={setValue}
					goBack={pages.length > 0}
					onGoBack={handleRemovePage}
				/>
				<CommandList>
					{!page && (
						<Suggestions
							value={value}
							onClose={handleClose}
							onAddPage={handleAddPage}
							onNavigate={handleNavigate}
						/>
					)}
					{!!page && (
						<CommandGroup heading={page.heading !== undefined ? page.heading : page.name}>
							<div className="pt-1" />
							{React.createElement(page.component as any, {
								args: page.args,
								onClose: handleClose,
								onNavigate: handleNavigate
							})}
						</CommandGroup>
					)}
				</CommandList>
				<div className="flex items-center gap-4 border-t px-3 py-2 text-[10px] text-muted-foreground">
					<span className="flex items-center gap-1">
						<kbd className={KBD}>↑</kbd>
						<kbd className={KBD}>↓</kbd>
						Navigate
					</span>
					<span className="flex items-center gap-1">
						<kbd className={KBD}>↵</kbd>
						Open
					</span>
					<span className="flex items-center gap-1">
						<kbd className={KBD}>esc</kbd>
						Close
					</span>
				</div>
			</CommandDialog>
		)
	}

export default CommandPalette
